import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, Copy, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { api, toastError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const LANGUAGES = [
  { code: "en", flag: "🇬🇧", label: "English" },
  { code: "it", flag: "🇮🇹", label: "Italian" },
  { code: "es", flag: "🇪🇸", label: "Spanish" },
  { code: "de", flag: "🇩🇪", label: "German" },
];
const LANG_BY_CODE = Object.fromEntries(LANGUAGES.map((l) => [l.code, l]));

export default function Agents() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState("all");
  const [onlyEnabled, setOnlyEnabled] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (language !== "all") params.language = language;
      if (onlyEnabled) params.enabled = true;
      const res = await api.get("/agents", { params });
      setAgents(res.data);
    } catch (err) {
      toastError(err, "Failed to load agents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, onlyEnabled]);

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/agents/${confirmDelete.id}`);
      toast.success(`Deleted "${confirmDelete.name}"`);
      setConfirmDelete(null);
      fetchAll();
    } catch (err) {
      toastError(err, "Failed to delete agent");
    }
  };

  const onDuplicate = async (a) => {
    try {
      const res = await api.post(`/agents/${a.id}/duplicate`);
      toast.success("Agent duplicated");
      navigate(`/agents/${res.data.id}`);
    } catch (err) {
      toastError(err, "Failed to duplicate agent");
    }
  };

  const onToggleEnabled = async (a, value) => {
    try {
      await api.put(`/agents/${a.id}`, { enabled: value });
      setAgents((prev) =>
        prev.map((x) => (x.id === a.id ? { ...x, enabled: value } : x))
      );
    } catch (err) {
      toastError(err, "Failed to update agent");
    }
  };

  return (
    <div className="space-y-8" data-testid="agents-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">AI Agents</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Define the conversational agents the voice worker will run.
          </p>
        </div>
        <Button
          onClick={() => navigate("/agents/new")}
          data-testid="agents-new-btn"
        >
          <Plus className="h-4 w-4" /> New agent
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-border bg-card p-3">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Language</Label>
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger className="w-44" data-testid="agents-filter-lang">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All languages</SelectItem>
              {LANGUAGES.map((l) => (
                <SelectItem key={l.code} value={l.code}>
                  {l.flag} {l.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={onlyEnabled}
            onCheckedChange={setOnlyEnabled}
            data-testid="agents-filter-enabled"
          />
          <Label className="cursor-pointer text-sm">Only enabled</Label>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card">
        {loading ? (
          <div className="flex h-40 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : agents.length === 0 ? (
          <EmptyState onAdd={() => navigate("/agents/new")} />
        ) : (
          <Table data-testid="agents-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Language</TableHead>
                <TableHead>LLM</TableHead>
                <TableHead>TTS voice</TableHead>
                <TableHead>Enabled</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agents.map((a) => {
                const lang = LANG_BY_CODE[a.language];
                return (
                  <TableRow key={a.id} data-testid={`agents-row-${a.id}`}>
                    <TableCell>
                      <div className="font-medium">{a.name}</div>
                      {a.description && (
                        <div className="text-xs text-muted-foreground line-clamp-1">
                          {a.description}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-base mr-1">{lang?.flag}</span>
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">
                        {a.language}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px] uppercase mr-1">
                        {a.llm_provider}
                      </Badge>
                      <span className="text-xs font-mono">
                        {a.llm_model || "default"}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-[10px] truncate max-w-[180px]">
                      {a.tts_voice_id}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={a.enabled}
                        onCheckedChange={(v) => onToggleEnabled(a, v)}
                        data-testid={`agents-toggle-${a.id}`}
                      />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(a.updated_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/agents/${a.id}`)}
                        data-testid={`agents-edit-${a.id}`}
                      >
                        <Pencil className="h-4 w-4" /> Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onDuplicate(a)}
                        data-testid={`agents-duplicate-${a.id}`}
                      >
                        <Copy className="h-4 w-4" /> Duplicate
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setConfirmDelete(a)}
                        data-testid={`agents-delete-${a.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" /> Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent data-testid="agents-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete agent?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <b>{confirmDelete?.name}</b>. Workers
              will no longer be able to fetch this agent's config.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              data-testid="agents-delete-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div
      className="flex flex-col items-center gap-3 py-16 text-center"
      data-testid="agents-empty"
    >
      <div className="rounded-full bg-secondary p-3">
        <Bot className="h-6 w-6 text-muted-foreground" />
      </div>
      <div className="text-base font-medium">No agents yet</div>
      <p className="max-w-sm text-sm text-muted-foreground">
        Create your first AI agent. Define its language, prompts, voice and
        provider stack — workers will pick it up at runtime.
      </p>
      <Button onClick={onAdd} className="mt-2" data-testid="agents-empty-add">
        <Plus className="h-4 w-4" /> New agent
      </Button>
    </div>
  );
}
